import type { AgenticChatSessionSyncError } from 'features/embeddedSidebar/sessions/documentChatSessionRolodex'
import type {
  DocumentChatActiveTurn,
  DocumentChatStoredTurn,
} from 'features/embeddedSidebar/sessions/documentChatSessionSchemas'
import { hasInProgressDocChatTurn } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatConversation'
import type { DocChatStreamUpdate } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import { subscribeDocChatStreamUpdates } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatInFlightStore'
import { useRenderedDocChatStorageOwner } from 'features/knowledgeDocumentViewer/hooks/useRenderedDocChatStorageOwner'
import type {
  DocChatConversation,
  DocumentTab,
} from 'features/knowledgeDocumentViewer/types'
import {
  clearAgenticChatComposerDraftsForSession,
  getAgenticChatComposerDraft,
  persistAgenticChatComposerDrafts,
} from 'features/knowledgeDocumentViewer/utils/agenticChatComposerDraft'
import { NEW_AGENTIC_CHAT_TAB_LABEL } from 'features/knowledgeDocumentViewer/utils/agenticChatTabFactories'
import {
  pendingAgenticConversationKey,
  resolveAgenticConversationIdentity,
} from 'features/knowledgeDocumentViewer/utils/agenticConversationIdentity'
import {
  type DocChatPreview,
  getDocChatPreviewFromTab,
} from 'features/knowledgeDocumentViewer/utils/docChatPreview'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type AgenticChatSessionSyncStatus =
  | 'idle'
  | 'archiving'
  | 'unarchiving'
  | 'deleting'
  | 'hydrating'
  | 'error'

export interface RecordedAgenticChatSession {
  archived?: boolean
  /** Local membership while history queries catch up to an archive change. */
  archivedOverride?: boolean
  /** Unsent composer text. `''` after submit; omitted when never typed. */
  composerDraft?: string
  conversation: DocChatConversation
  customTitle?: string
  isHydrated?: boolean
  key: string
  lastCompletedTurnId?: string
  activeTurn?: DocumentChatActiveTurn
  pendingUserQuery?: string
  persistError?: AgenticChatSessionSyncError
  pinned?: boolean
  pinnedAt?: number
  preview?: DocChatPreview
  siteId: string
  snapshotPath?: string
  sourceTabId: string
  storedTurns?: DocumentChatStoredTurn[]
  syncStatus?: AgenticChatSessionSyncStatus
  title: string
  updatedAt: number
  visibleTurnCount?: number
}

function hasAgenticChatSessionContent(
  session: RecordedAgenticChatSession
): boolean {
  return (
    session.conversation.turns.length > 0 ||
    (session.composerDraft?.trim().length ?? 0) > 0
  )
}

function userChosenAgenticChatTitle({
  firstTurnQuery,
  label,
}: {
  firstTurnQuery?: string
  label?: string
}): string | undefined {
  if (
    label === undefined ||
    label === NEW_AGENTIC_CHAT_TAB_LABEL ||
    label === firstTurnQuery
  ) {
    return undefined
  }
  return label
}

function mergedAgenticChatTitle({
  existingCustomTitle,
  firstTurnQuery,
  tabCustomLabel,
}: {
  existingCustomTitle?: string
  firstTurnQuery?: string
  tabCustomLabel?: string
}): { customTitle?: string; title: string } {
  const customTitle =
    existingCustomTitle ??
    userChosenAgenticChatTitle({
      firstTurnQuery,
      label: tabCustomLabel,
    })
  return {
    title:
      customTitle ??
      firstTurnQuery ??
      tabCustomLabel ??
      NEW_AGENTIC_CHAT_TAB_LABEL,
    ...(customTitle === undefined ? {} : { customTitle }),
  }
}

function retireSupersededPendingKeys({
  conversation,
  key,
  sessionsByKey,
  tabId,
}: {
  conversation: DocChatConversation
  key: string
  sessionsByKey: Map<string, RecordedAgenticChatSession>
  tabId: string
}) {
  const tabPendingKey = pendingAgenticConversationKey(tabId)
  if (key !== tabPendingKey) sessionsByKey.delete(tabPendingKey)
  const recordedPendingKey = conversation.pendingSessionKey
  if (recordedPendingKey !== undefined && recordedPendingKey !== key) {
    sessionsByKey.delete(recordedPendingKey)
  }
}

function recordedSessionForOpenTab({
  conversation,
  key,
  sessionsByKey,
  tabId,
}: {
  conversation: DocChatConversation
  key: string
  sessionsByKey: Map<string, RecordedAgenticChatSession>
  tabId: string
}): RecordedAgenticChatSession | undefined {
  const pendingSessionKey = conversation.pendingSessionKey
  return (
    sessionsByKey.get(key) ??
    sessionsByKey.get(pendingAgenticConversationKey(tabId)) ??
    (pendingSessionKey === undefined
      ? undefined
      : sessionsByKey.get(pendingSessionKey))
  )
}

function hasSameAgenticChatRecency(
  existing: DocChatConversation,
  next: DocChatConversation
): boolean {
  if (existing === next) return true
  return (
    existing.turns.length === next.turns.length &&
    existing.turns.at(-1)?.id === next.turns.at(-1)?.id
  )
}

function isLiveAgenticChatSend(
  conversation?: Pick<DocChatConversation, 'id' | 'turns'>
): boolean {
  if (!conversation || conversation.turns.length === 0) return false
  return (
    conversation.id === undefined ||
    hasInProgressDocChatTurn(
      conversation.turns.filter((turn) => !turn.restoredRunning)
    )
  )
}

/** Recency is last user send; restoring history must not advance it. */
export function nextAgenticChatSessionUpdatedAt({
  conversation,
  conversationUnchanged,
  existingUpdatedAt,
  openedAt,
  now,
}: {
  conversation?: Pick<DocChatConversation, 'id' | 'turns'>
  conversationUnchanged: boolean
  existingUpdatedAt?: number
  openedAt?: number
  now: number
}): number {
  if (existingUpdatedAt !== undefined) {
    return conversationUnchanged ? existingUpdatedAt : now
  }
  if (isLiveAgenticChatSend(conversation)) return now
  return openedAt ?? 0
}

export function mergeAgenticChatSessions({
  currentSessions,
  openTabs,
  siteId,
  now = Date.now(),
}: {
  currentSessions: RecordedAgenticChatSession[]
  openTabs: DocumentTab[]
  siteId: string | undefined
  now?: number
}): RecordedAgenticChatSession[] {
  if (!siteId) return []

  const sessionsByKey = new Map(
    currentSessions
      .filter((session) => session.siteId === siteId)
      .map((session) => [session.key, session])
  )

  const openSessionKeys = new Set<string>()

  openTabs.forEach((tab) => {
    const identity = resolveAgenticConversationIdentity(tab, siteId)
    if (!identity) return

    const { conversation, key } = identity
    openSessionKeys.add(key)
    const existingSession = recordedSessionForOpenTab({
      conversation,
      key,
      sessionsByKey,
      tabId: tab.id,
    })
    retireSupersededPendingKeys({
      conversation,
      key,
      sessionsByKey,
      tabId: tab.id,
    })
    const conversationUnchanged =
      existingSession !== undefined &&
      hasSameAgenticChatRecency(existingSession.conversation, conversation)

    const { customTitle, title } = mergedAgenticChatTitle({
      existingCustomTitle: existingSession?.customTitle,
      firstTurnQuery: conversation.turns[0]?.query,
      tabCustomLabel: tab.customLabel,
    })
    const preview = getDocChatPreviewFromTab(tab)
    const tabComposerDraft = getAgenticChatComposerDraft(tab)
    const composerDraft =
      tabComposerDraft !== undefined
        ? tabComposerDraft
        : existingSession?.composerDraft
    sessionsByKey.set(key, {
      conversation,
      key,
      siteId,
      sourceTabId: tab.id,
      title,
      updatedAt: nextAgenticChatSessionUpdatedAt({
        conversation,
        conversationUnchanged,
        existingUpdatedAt: existingSession?.updatedAt,
        openedAt: tab.openedAt,
        now,
      }),
      archived: existingSession?.archived,
      isHydrated: true,
      lastCompletedTurnId: existingSession?.lastCompletedTurnId,
      persistError: existingSession?.persistError,
      pinned: existingSession?.pinned,
      pinnedAt: existingSession?.pinnedAt,
      syncStatus: existingSession?.syncStatus,
      visibleTurnCount: existingSession?.visibleTurnCount,
      ...(composerDraft === undefined ? {} : { composerDraft }),
      ...(customTitle === undefined ? {} : { customTitle }),
      ...(preview === undefined ? {} : { preview }),
    })
  })

  // Open empty tabs stay internal for first-send recency and clearing stored drafts.
  return [...sessionsByKey.values()]
    .filter(
      (session) =>
        openSessionKeys.has(session.key) ||
        hasAgenticChatSessionContent(session)
    )
    .sort((left, right) => right.updatedAt - left.updatedAt)
}

/** Keep last-send recency while a turn streams; stamp now only for a new user turn. */
export function patchAgenticChatSessionConversation({
  currentSessions,
  keys,
  conversation,
  siteId,
  now = Date.now(),
}: {
  currentSessions: RecordedAgenticChatSession[]
  keys: string[]
  conversation: DocChatConversation
  siteId: string
  now?: number
}): RecordedAgenticChatSession[] {
  const nextKey = conversation.id ?? conversation.pendingSessionKey ?? keys[0]
  if (!nextKey) return currentSessions
  const matchKeys = new Set(
    [...keys, conversation.id, conversation.pendingSessionKey].filter(
      (key): key is string => key !== undefined && key.length > 0
    )
  )
  let matched: RecordedAgenticChatSession | undefined
  const nextSessions: RecordedAgenticChatSession[] = []
  currentSessions.forEach((session) => {
    if (session.siteId === siteId && matchKeys.has(session.key)) {
      if (matched === undefined) matched = session
      return
    }
    nextSessions.push(session)
  })
  if (matched === undefined) return currentSessions
  nextSessions.push({
    ...matched,
    conversation,
    isHydrated: true,
    key: nextKey,
    updatedAt: hasSameAgenticChatRecency(matched.conversation, conversation)
      ? matched.updatedAt
      : now,
  })
  return nextSessions.sort((left, right) => right.updatedAt - left.updatedAt)
}

function applyStreamUpdateToHistory(
  current: {
    openTabs: DocumentTab[]
    sessions: RecordedAgenticChatSession[]
    siteId: string | undefined
  },
  update: DocChatStreamUpdate
): {
  openTabs: DocumentTab[]
  sessions: RecordedAgenticChatSession[]
  siteId: string | undefined
} {
  const sessions = patchAgenticChatSessionConversation({
    currentSessions: current.sessions,
    keys: update.keys,
    conversation: update.conversation,
    siteId: update.siteId,
  })
  if (sessions === current.sessions) return current
  return { ...current, sessions }
}

const EMPTY_DOCUMENT_TABS: DocumentTab[] = []

function createAgenticChatHistoryState({
  currentSessions,
  openTabs,
  siteId,
}: {
  currentSessions: RecordedAgenticChatSession[]
  openTabs: DocumentTab[]
  siteId: string | undefined
}): {
  openTabs: DocumentTab[]
  sessions: RecordedAgenticChatSession[]
  siteId: string | undefined
} {
  return {
    openTabs,
    siteId,
    sessions: mergeAgenticChatSessions({
      currentSessions,
      openTabs,
      siteId,
    }),
  }
}

export function useAgenticChatSessionHistory(
  openTabs: DocumentTab[] | undefined,
  siteId: string | undefined
): {
  deleteSession: (sessionKey: string) => void
  pinSession: (sessionKey: string, pinned: boolean) => void
  renameSession: (sessionKey: string, title: string) => void
  sessions: RecordedAgenticChatSession[]
} {
  const tabs = openTabs?.length ? openTabs : EMPTY_DOCUMENT_TABS
  const [history, setHistory] = useState(() =>
    createAgenticChatHistoryState({
      currentSessions: [],
      openTabs: tabs,
      siteId,
    })
  )

  if (tabs !== history.openTabs || siteId !== history.siteId) {
    setHistory(
      createAgenticChatHistoryState({
        currentSessions: history.sessions,
        openTabs: tabs,
        siteId,
      })
    )
  }

  useEffect(() => {
    return subscribeDocChatStreamUpdates((update) => {
      setHistory((current) => applyStreamUpdateToHistory(current, update))
    })
  }, [])

  const storageOwnerRef = useRenderedDocChatStorageOwner()
  useEffect(() => {
    persistAgenticChatComposerDrafts(storageOwnerRef.current, history.sessions)
  }, [history.sessions, storageOwnerRef])

  const sessionsRef = useRef(history.sessions)
  sessionsRef.current = history.sessions
  useEffect(() => {
    const persist = () =>
      persistAgenticChatComposerDrafts(
        storageOwnerRef.current,
        sessionsRef.current
      )
    window.addEventListener('pagehide', persist)
    return () => {
      window.removeEventListener('pagehide', persist)
      persist()
    }
  }, [storageOwnerRef])

  const deleteSession = useCallback(
    (sessionKey: string) => {
      if (siteId) {
        const session = history.sessions.find(
          (candidate) => candidate.key === sessionKey
        )
        clearAgenticChatComposerDraftsForSession({
          conversationId: session?.conversation.id,
          sessionKey,
          siteId,
        })
      }
      setHistory((current) => ({
        ...current,
        sessions: current.sessions.filter(
          (session) => session.key !== sessionKey
        ),
      }))
    },
    [history.sessions, siteId]
  )

  const renameSession = useCallback((sessionKey: string, title: string) => {
    setHistory((current) => ({
      ...current,
      sessions: current.sessions.map((session) =>
        session.key === sessionKey
          ? { ...session, customTitle: title, title }
          : session
      ),
    }))
  }, [])

  const pinSession = useCallback((sessionKey: string, pinned: boolean) => {
    setHistory((current) => ({
      ...current,
      sessions: current.sessions.map((session) => {
        if (session.key !== sessionKey) return session
        if (!pinned) return { ...session, pinned: false }
        return { ...session, pinned: true, pinnedAt: Date.now() }
      }),
    }))
  }, [])

  const sessions = useMemo(
    () => history.sessions.filter(hasAgenticChatSessionContent),
    [history.sessions]
  )

  return {
    deleteSession,
    pinSession,
    renameSession,
    sessions,
  }
}
